import { Action, MovementAction } from "@opstel/query/v2/models/action";
import { ShuntingUnit } from "@opstel/query/models/shuntingUnit";
import { TrackCode } from "@opstel/query/models/track";
import { getSBDActionStyle } from "../../../SBDAction/styles";
import { useTheme } from "@shared/EDS/contexts/useTheme";
import {
  DATETIME_FORMAT_NO_DATE,
  DURATION_FORMAT_NO_SECONDS,
} from "@shared/common/utils/luxonHelpers";
import { ErrorState } from "@shared/common/components/State/ErrorState";
import { DateTime } from "luxon";
import { useBorderTrains } from "./useBorderTrainsQuery";
import { LoadingState } from "@shared/common/components/State/LoadingState";
import { useShuntingUnitActions } from "./useShuntingUnitActionsQuery";

interface ChainDescriptionProps {
  readonly shuntingUnit: ShuntingUnit;
}

export function ChainDescription({ shuntingUnit }: ChainDescriptionProps) {
  const { theme } = useTheme();
  const { data: actions, isPending, isError, error } = useShuntingUnitActions(shuntingUnit.guid);

  if (isPending) return <LoadingState />;
  if (isError) return <div>{error.message}</div>;

  const firstAction = actions.at(0);
  const lastAction = actions.at(-1);
  const movementActions = actions.filter(Action.isMoveOrTeleport);

  if (!firstAction || !lastAction) return <ErrorState />;

  const trainUnitKind = shuntingUnit.planTrainUnits.inAnyDirection().at(0)?.kind;
  const trainUnitStyle = trainUnitKind && getSBDActionStyle(trainUnitKind, theme);

  return (
    <div className="flex flex-col gap-s">
      <h4 className="m-0 font-medium">Materieelketen</h4>
      <div className="flex gap-s">
        <span className="text-s font-medium">
          {ShuntingUnit.getTrainTypes(shuntingUnit).join(" + ")}
        </span>
        <Dot color={trainUnitStyle?.backgroundColor} />
        <span className="ml-auto text-s text-subtle">
          Overstand{" "}
          {lastAction.endTime
            .diff(firstAction.startTime)
            .rescale()
            .toFormat(DURATION_FORMAT_NO_SECONDS)}
        </span>
      </div>
      <ol className="m-0 list-none p-0">
        {movementActions.map((action) => (
          <Movement key={action.id} action={action} />
        ))}
      </ol>
      {(Action.isSplitOrCombine(firstAction) || Action.isSplitOrCombine(lastAction)) && (
        <ArrivingDepartingTrains shuntingUnitId={shuntingUnit.guid} />
      )}
    </div>
  );
}

function ArrivingDepartingTrains({ shuntingUnitId }: { readonly shuntingUnitId: string }) {
  const { data: borderTrains, isPending, isError } = useBorderTrains(shuntingUnitId);

  if (isPending) return <LoadingState />;
  if (isError) return <ErrorState />;

  return (
    <div
      className="flex flex-col gap-m border-t border-subtle pt-m"
      aria-label="Aankomende en vertrekkende treinen"
    >
      <div>
        <span className="text-s font-medium">Aankomst op de knoop</span>
        {borderTrains.arrivingTrains.map((arrivingTrain) => (
          <Track
            type="Aankomst"
            key={arrivingTrain.name}
            trainName={arrivingTrain.name}
            dateTime={arrivingTrain.datetime}
            trackName={arrivingTrain.track}
          />
        ))}
      </div>
      <div>
        <span className="text-s font-medium">Vertrek van de knoop</span>
        {borderTrains.departingTrains.map((departingTrain) => (
          <Track
            key={departingTrain.name}
            type="Vertrek"
            trainName={departingTrain.name}
            dateTime={departingTrain.datetime}
            trackName={departingTrain.track}
          />
        ))}
      </div>
    </div>
  );
}

function Movement({ action }: { readonly action: MovementAction }) {
  const firstTrack = action.tracks.at(0);
  const lastTrack = action.tracks.at(-1);
  const showsDepartureTrack = !!firstTrack && !TrackCode.isForeignTrack(firstTrack);
  const showsArrivalTrack = !!lastTrack && !TrackCode.isForeignTrack(lastTrack);

  return (
    <li className="m-0 border-t border-subtle py-xs first:border-t-0 first:pt-0">
      {showsDepartureTrack && (
        <Track
          type="Vertrek"
          trainName={action.movementCode}
          dateTime={action.startTime}
          trackName={firstTrack}
        />
      )}
      {showsArrivalTrack && (
        <Track
          type="Aankomst"
          trainName={showsDepartureTrack ? undefined : action.movementCode}
          dateTime={action.endTime}
          trackName={lastTrack}
        />
      )}
    </li>
  );
}

function Track({
  type,
  trainName,
  dateTime,
  trackName,
}: {
  readonly type: "Aankomst" | "Vertrek";
  readonly trainName?: string;
  readonly dateTime?: DateTime;
  readonly trackName?: string;
}) {
  return (
    <div className="flex gap-m text-s text-subtle" aria-label={type}>
      <span className="w-1/6">{trainName}</span>
      <span> {type === "Aankomst" ? "A" : "V"}</span>
      <span> {dateTime?.toFormat(DATETIME_FORMAT_NO_DATE)}</span>
      <span> {trackName}</span>
    </div>
  );
}

function Dot({ color }: { readonly color?: string }) {
  return (
    <span className="h-[8px] w-[8px] self-center rounded-full" style={{ backgroundColor: color }} />
  );
}
